import { useEffect, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Wallet, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

function today() { return new Date().toISOString().slice(0, 10); }
const CATEGORIES = ["Rent", "Electricity", "Water", "Salaries", "Consumables", "Marketing", "Transport", "Repairs", "Office Supplies", "Other"];
const MODES = ["cash", "bank", "upi", "cheque", "other"];
const empty = { date: today(), category: "Rent", description: "", amount: 0, payment_mode: "cash", bank_account_id: "" };

export default function Expenses() {
  const [list, setList] = useState([]);
  const pg = usePagination(list);
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(empty);

  const load = () => api.get("/expenses").then(r => setList(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => {
    load();
    api.get("/bank-accounts").then(r => setBanks(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    try {
      const payload = { ...f, amount: Number(f.amount),
        bank_account_id: f.payment_mode === "bank" ? (f.bank_account_id || null) : null,
        description: f.description || null };
      if (editId) await api.patch(`/expenses/${editId}`, payload);
      else await api.post("/expenses", payload);
      toast.success(editId ? "Updated" : "Added");
      setF(empty); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const edit = (e) => { setF({ ...empty, ...e, bank_account_id: e.bank_account_id || "" }); setEditId(e.id); setOpen(true); };
  const del = async (id) => {
    if (!confirm("Delete this expense?")) return;
    try { await api.delete(`/expenses/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const total = list.reduce((s, e) => s + e.amount, 0);

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Wallet className="w-3 h-3"/> Accounts · Workshop & business expenses (Owner / Manager)</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Expenses</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="border border-border bg-card rounded-sm px-4 py-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total Recorded</div>
            <div className="font-display font-black text-xl text-destructive" data-testid="exp-total">{inr(total)}</div>
          </div>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-exp-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">
                <Plus className="w-4 h-4 mr-1"/> New Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm bg-card border-border max-w-lg" onKeyDownCapture={handleSimpleFormNav}>
              <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} Expense</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Date</Label><Input data-testid="exp-date" type="date" value={f.date} onChange={(e) => setF(x => ({ ...x, date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Category</Label>
                    <Select value={f.category} onValueChange={(v) => setF(x => ({ ...x, category: v }))}>
                      <SelectTrigger data-testid="exp-category" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                      <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Amount ₹</Label><Input data-testid="exp-amount" type="number" value={f.amount} onChange={(e) => setF(x => ({ ...x, amount: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border text-right"/></div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Payment Mode</Label>
                    <Select value={f.payment_mode} onValueChange={(v) => setF(x => ({ ...x, payment_mode: v }))}>
                      <SelectTrigger data-testid="exp-mode" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                      <SelectContent>{MODES.map(m => <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                {f.payment_mode === "bank" && (
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Bank Account</Label>
                    <Select value={f.bank_account_id} onValueChange={(v) => setF(x => ({ ...x, bank_account_id: v }))}>
                      <SelectTrigger className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select bank"/></SelectTrigger>
                      <SelectContent>{banks.map(b => <SelectItem key={b.id} value={b.id}>{b.name} · {b.bank_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Description</Label><Textarea data-testid="exp-desc" value={f.description} onChange={(e) => setF(x => ({ ...x, description: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/></div>
                <Button data-testid="save-exp-btn" onClick={save} disabled={!f.amount || !f.date} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
                  {editId ? "Update" : "Save Expense"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Date</th><th className="text-left p-3">Category</th>
              <th className="text-left p-3">Description</th><th className="text-left p-3">Mode</th>
              <th className="text-right p-3">Amount</th><th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No expenses recorded yet</td></tr>}
            {pg.pageItems.map(e => (
              <tr key={e.id} data-testid={`exp-row-${e.id}`} className="border-t border-border">
                <td className="p-3 font-mono text-xs">{e.date}</td>
                <td className="p-3 text-xs uppercase tracking-wider">{e.category}</td>
                <td className="p-3 text-xs text-muted-foreground">{e.description || "-"}</td>
                <td className="p-3 text-xs uppercase">{e.payment_mode}</td>
                <td className="p-3 text-right font-mono text-destructive">{inr(e.amount)}</td>
                <td className="p-3 text-right">
                  <Button data-testid={`edit-exp-${e.id}`} size="sm" variant="ghost" onClick={() => edit(e)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                  <Button data-testid={`del-exp-${e.id}`} size="sm" variant="ghost" onClick={() => del(e.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
